import { apiRequest } from "@core/services/api/apiRequest";
import { orgInvitationEventBus } from "@platform/events/orgInvitation.events";
import { OrganizationType } from "@platform/types/orgInvitation.types";

type Args = {
  shortcode?: string;
  organization?: OrganizationType;
  code?: string;
  type: "lookupOrg" | "shortcode" | "code";
};

/**
 * Validate an invitation code either with a code (coming from the code input)
 * or with an organization (coming from the lookup input).
 * Emit events based on the type of validation.
 * */
export const validateOrgInvitation = async ({
  shortcode,
  organization,
  code,
  type,
}: Args) => {
  // If the type is lookupOrg, the organization is required
  if (type === "lookupOrg" && !organization) {
    throw new Error("Organization is required");
  }

  // If the type is code or shortcode, the code/shortcode is required
  if ((type === "shortcode" || type === "code") && !shortcode && !code) {
    throw new Error("Code is required");
  }

  // api expects a code so we need to get the code from the organization if the type is lookupOrg
  const codeToValidate =
    type === "lookupOrg"
      ? organization?.invitation_code
      : type === "code"
      ? code
      : shortcode;

  // Emit events based on the type of validation
  if (type === "lookupOrg") {
    orgInvitationEventBus.emitEvent("lookupOrg:submitted", {
      organization: organization!,
    });
  }
  if (type === "shortcode" || type === "code") {
    orgInvitationEventBus.emitEvent("code:submitted", {
      code: codeToValidate!,
    });
  }

  try {
    const response = await apiRequest(
      `/invitation/${codeToValidate}/`,
      "GET",
      null,
      false
    );

    // Emit events based on the type of validation
    if (type === "lookupOrg") {
      orgInvitationEventBus.emitEvent("lookupOrg:success", {
        organization: organization!,
        response,
      });
    }
    if (type === "shortcode" || type === "code") {
      orgInvitationEventBus.emitEvent("code:success", {
        code: codeToValidate!,
        response,
      });
    }

    return response;
  } catch (error) {
    // Emit events based on the type of validation
    if (type === "lookupOrg") {
      orgInvitationEventBus.emitEvent("lookupOrg:failed", {
        organization: organization!,
      });
    }
    if (type === "shortcode" || type === "code") {
      orgInvitationEventBus.emitEvent("code:failed", {
        code: codeToValidate!,
      });
    }

    throw error;
  }
};

export default validateOrgInvitation;

